import { useCallback, useEffect, useState } from 'react';
import { GRAPH_REFRESH_INTERVAL_MS } from '../../config/constants';
import useLatestRequest from '../../hooks/useLatestRequest';

/**
 * Общий цикл загрузки данных карточки: запрос → последний запрос побеждает →
 * ошибка → периодическое обновление.
 *
 * Вынесено отдельно, потому что этот блок нужен каждому источнику данных карточки
 * (агрегат, временной ряд, тепловая карта), а копии успевали разойтись по мелочам —
 * например, по тому, показывать ли спиннер при фоновом обновлении.
 *
 * `enabled` нужен, чтобы карточка не дёргала сервер, пока не выбран период или пока
 * пользователь не переключился на вкладку динамики.
 *
 * Смена параметров (период, фильтры) запрашивает данные заново, даже если прежний запрос ещё
 * в полёте, а его ответ отбрасывается (`useLatestRequest`). Пропускается только опрос по таймеру.
 */
const useNodeRequest = ({ fetcher, params, errorText, enabled = true, poll = true }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const gate = useLatestRequest();

  const load = useCallback(
    async (showSpinner = true) => {
      if (!enabled) {
        return;
      }

      const request = gate.begin({ background: showSpinner === false });
      if (!request) {
        return;
      }

      if (showSpinner) {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await fetcher(params);
        if (request.isCurrent()) {
          setData(result);
        }
      } catch {
        if (request.isCurrent()) {
          setError(errorText);
        }
      } finally {
        const isCurrent = request.isCurrent();
        request.end();
        if (showSpinner && isCurrent) {
          setIsLoading(false);
        }
      }
    },
    [enabled, fetcher, params, errorText, gate]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!enabled || !poll) {
      return undefined;
    }

    const interval = setInterval(() => load(false), GRAPH_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, poll, load]);

  return { data, isLoading, error, reload: load };
};

export default useNodeRequest;
